/**
 * Utility functions for comparing cart items and determining what needs to be created, updated, or deleted
 */

export type ProductInput = {
    [category: string]: { quantity: number; value: string; manufacturerProductId?: string; color?: string };
};

export type ExistingProduct = {
    Id: string;
    ProductName: string;
    ItemCount: number;
    ProductCategory: string;
    Project_Id: string;
    ManufacturerProductId?: string | null;
};

export type DiffResult = {
    toUpdate: ExistingProduct[];
    toDelete: ExistingProduct[];
    toCreate: {
        ItemCount: number;
        ManufacturerProductId?: string;
    }[];
};

/**
 * Compares existing cart items with new selections and determines what needs to be created, updated, or deleted
 */
export function diffProducts(existing: ExistingProduct[], input: ProductInput): DiffResult {
    const toUpdate: ExistingProduct[] = [];
    const toDelete: ExistingProduct[] = [];
    const toCreate: DiffResult["toCreate"] = [];

    // Build a map of input items for quick lookup
    const inputMap = new Map<string, { quantity: number; value: string; manufacturerProductId?: string }>();
    for (const [category, { quantity, value, manufacturerProductId }] of Object.entries(input)) {
        const key = `${category}|${value}|${manufacturerProductId || ""}`;
        inputMap.set(key, { quantity, value, manufacturerProductId });
    }

    const seenKeys = new Set<string>();

    // Check existing items against input
    for (const existingItem of existing) {
        const key = `${existingItem.ProductCategory}|${existingItem.ProductName}|${
            existingItem.ManufacturerProductId || ""
        }`;
        seenKeys.add(key);
        const inputEntry = inputMap.get(key);

        if (!inputEntry) {
            // Item exists in DB but not in input - mark for deletion
            toDelete.push(existingItem);
        } else if (existingItem.ItemCount !== inputEntry.quantity) {
            // Item exists but quantity changed
            // If quantity is 0, delete instead of updating
            if (inputEntry.quantity === 0) {
                toDelete.push(existingItem);
            } else {
                // Mark for update
                toUpdate.push({ ...existingItem, ItemCount: inputEntry.quantity });
            }
        }
    }

    // Check for new entries not in existing DB
    for (const [category, { quantity, value, manufacturerProductId }] of Object.entries(input)) {
        // Skip items without a value (product not selected)
        if (!value) {
            continue;
        }
        
        const key = `${category}|${value}|${manufacturerProductId || ""}`;
        if (!seenKeys.has(key)) {
            // Item in input but not in DB - mark for creation
            // Only create if ManufacturerProductId is provided (required for materials) and quantity > 0
            if (manufacturerProductId && quantity > 0) {
                toCreate.push({
                    ItemCount: quantity,
                    ManufacturerProductId: manufacturerProductId,
                });
            } else if (!manufacturerProductId && quantity > 0) {
                // Log warning for items that should be created but don't have manufacturerProductId
                console.warn(`Cannot create cart item for ${category} (${value}): ManufacturerProductId is missing. This may be a product with color options that hasn't been fully selected yet.`);
            }
        }
    }

    return { toUpdate, toDelete, toCreate };
}
